import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import savsService from '../services/savsService';
import SavStatsBanner from '../components/savs/SavStatsBanner';
import CreateSavModal from '../components/savs/CreateSavModal';
import SavDetailsModal from '../components/savs/SavDetailsModal';

const getStatusColor = (status) => {
  switch (status) {
    case 'non_traite':
      return 'warning';
    case 'en_cours':
      return 'info';
    case 'fini':
      return 'success';
    default:
      return 'default';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'non_traite':
      return 'Non traité';
    case 'en_cours':
      return 'En cours';
    case 'fini':
      return 'Fini';
    default:
      return status;
  }
};

const getActionLabel = (action) => {
  const labels = {
    remboursement: 'Remboursement',
    changement: 'Changement',
    avoir: 'Avoir',
    reparation: 'Réparation',
    autre: 'Autre',
  };
  return labels[action] || action;
};

const Savs = () => {
  const { isAuthenticated } = useAuth();
  const [savs, setSavs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedSav, setSelectedSav] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSavs();
      fetchStats();
    }
  }, [isAuthenticated, page, rowsPerPage, search, statusFilter]);

  const fetchSavs = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (search) filters.search = search;

      const response = await savsService.getAllSavs(page + 1, rowsPerPage, filters);
      setSavs(response.data.savs || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la récupération des SAV');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await savsService.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Erreur lors de la récupération des statistiques:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleViewDetails = (sav) => {
    setSelectedSav(sav);
    setDetailsModalOpen(true);
  };

  const handleCreateSuccess = () => {
    fetchSavs();
    fetchStats();
  };

  const handleUpdateSuccess = () => {
    fetchSavs();
    fetchStats();
  };

  const handleDeleteSuccess = () => {
    fetchSavs();
    fetchStats();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Vous devez être connecté pour voir les SAV.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Service Après-Vente (SAV)</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          Créer un SAV
        </Button>
      </Box>

      {/* Bandeau de statistiques */}
      <SavStatsBanner stats={stats} loading={statsLoading} />

      {/* Filtres */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          value={search}
          onChange={handleSearchChange}
          placeholder="Rechercher..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Statut</InputLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Statut"
          >
            <MenuItem value="">Tous les statuts</MenuItem>
            <MenuItem value="non_traite">Non traité</MenuItem>
            <MenuItem value="en_cours">En cours</MenuItem>
            <MenuItem value="fini">Fini</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : savs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Aucun SAV trouvé
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {search || statusFilter
              ? 'Aucun SAV ne correspond à vos critères de recherche.'
              : "Vous n'avez pas encore créé de SAV."}
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Commande</TableCell>
                  <TableCell>Raison</TableCell>
                  <TableCell>Problème</TableCell>
                  <TableCell>Produit</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Date de création</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {savs.map((sav) => (
                  <TableRow key={sav.id} hover>
                    <TableCell>#{sav.id}</TableCell>
                    <TableCell>
                      {sav.order ? (
                        <>
                          {sav.order.orderNumber}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {sav.order.user
                              ? `${sav.order.user.first_name} ${sav.order.user.last_name}`
                              : `${sav.order.billingFirstName} ${sav.order.billingLastName}`}
                          </Typography>
                        </>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{sav.reason}</TableCell>
                    <TableCell>{sav.problem}</TableCell>
                    <TableCell>
                      {sav.product ? sav.product.name : '-'}
                    </TableCell>
                    <TableCell>{getActionLabel(sav.action)}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(sav.status)}
                        color={getStatusColor(sav.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(sav.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(sav)}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}

      {/* Modals */}
      <CreateSavModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <SavDetailsModal
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedSav(null);
        }}
        sav={selectedSav}
        onUpdate={handleUpdateSuccess}
        onDelete={handleDeleteSuccess}
      />
    </Box>
  );
};

export default Savs;
